/**
 * WEB: entrada vinda do TirvuAdm — "Acesso do Colaborador" do login do administrador (28/10/2026).
 *
 * Lá a pessoa digita CPF e senha do app, escolhe a empresa, e a tela pede o token ao t-credentials
 * (o MESMO Login/login do app) e abre esta rota com o token no FRAGMENTO:
 *   /entrar#origem=tirvuadm&token=<jwt>&id=<id_adm_colaborador>&hash=<TIRVU ID>
 * O fragmento não vai para servidor nenhum nem para o Referer, e é apagado do endereço assim que o
 * módulo carrega (antes de qualquer render), para o token não ficar na barra nem no histórico.
 *
 * Daqui em diante é o login normal do app (signIn com o token) e a pessoa vai DIRETO para a Home,
 * sem intro, permissões nem a tela de cadastro da biometria (pedido de 28/10/2026). O perfil carrega
 * na própria Home (o signIn já dispara o /iniciar), e a face já cadastrada no servidor é importada em
 * segundo plano, como no login do app.
 *
 * Outra conta já logada neste navegador: sai dela antes — o signIn sozinho, na MESMA empresa, mantém
 * o cache da pessoa anterior até o /iniciar responder. Se ela tiver registros esperando envio, não
 * troca (mesma regra do "Sair": nada pode subir com o token da pessoa errada).
 *
 * Link forjado ("login CSRF": alguém manda o link com o token DELE para outra pessoa entrar na conta
 * errada): sem a origem do TirvuAdm no Referer, a rota pede confirmação em vez de entrar sozinha.
 */
import { useAuth } from "@/contexts/AuthContext";
import { useRouter } from "@/navigation";
import { tenantHashDaSessao } from "@/services/Api";
import { FaceBiometria } from "@/services/FaceBiometria";
import { SyncService } from "@/services/Sync";
import { MaterialCommunityIcons } from "@/ui/icons";
import { saveColaboradorChoice } from "@/utils/appEnvironmentStorage";
import { useCallback, useEffect, useRef, useState } from "react";
import s from "./entrar.module.css";

type Pedido = { token: string; id: string; hash: string };

const JWT = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;
const HASH_VALIDO = /^[A-Za-z0-9_-]{1,60}$/;

/** Lido UMA vez, quando o módulo carrega — e o fragmento sai do endereço na mesma hora. */
const PEDIDO: Pedido | null = (() => {
  try {
    if (location.pathname.replace(/\/+$/, "") !== "/entrar" || !location.hash) return null;
    const p = new URLSearchParams(location.hash.slice(1));
    history.replaceState(history.state, "", location.pathname);
    const token = (p.get("token") ?? "").trim();
    const id = (p.get("id") ?? "").trim();
    const hash = (p.get("hash") ?? "").trim().toUpperCase();
    if (p.get("origem") !== "tirvuadm" || !JWT.test(token) || !/^\d+$/.test(id) || !HASH_VALIDO.test(hash)) return null;
    return { token, id, hash };
  } catch {
    return null;
  }
})();

/**
 * Veio da tela de login do TirvuAdm? Domínios da Tirvu (adm.tirvumais.com.br…), o ambiente local e o
 * que o build declarar em VITE_ORIGENS_TIRVUADM (origens ou hosts, separados por vírgula).
 */
function veioDoTirvuAdm(): boolean {
  let host = "";
  try {
    host = new URL(document.referrer).hostname.toLowerCase();
  } catch {
    return false;
  }
  const extras = String(import.meta.env.VITE_ORIGENS_TIRVUADM ?? "")
    .split(",")
    .map((o) => o.trim().toLowerCase().replace(/^https?:\/\//, "").replace(/[:/].*$/, ""))
    .filter(Boolean);
  return (
    host === "localhost" ||
    host === "127.0.0.1" ||
    host === "tirvumais.com.br" ||
    host.endsWith(".tirvumais.com.br") ||
    host === "tirvu.com.br" ||
    host.endsWith(".tirvu.com.br") ||
    extras.includes(host)
  );
}

type Etapa = "entrando" | "confirmar" | "erro";

export default function EntrarRoute() {
  const router = useRouter();
  const {
    isHydrated,
    isAuthenticated,
    idColaborador,
    isBiometricsSetupDone,
    signIn,
    signOut,
    refreshUserData,
    markBiometricsSetupDone,
    syncFirebaseToken,
  } = useAuth();
  const [etapa, setEtapa] = useState<Etapa>(() =>
    !PEDIDO ? "erro" : veioDoTirvuAdm() ? "entrando" : "confirmar",
  );
  const [erro, setErro] = useState(
    PEDIDO ? "" : "Este link de acesso não é válido ou já foi usado. Entre de novo pelo sistema da sua empresa.",
  );
  const comecou = useRef(false);

  const entrar = useCallback(async () => {
    if (!PEDIDO || comecou.current) return;
    comecou.current = true;
    setEtapa("entrando");
    try {
      // Outra conta neste navegador (ou outra empresa): sai dela antes, se não houver nada dela a enviar.
      if (isAuthenticated) {
        const hashAtual = await tenantHashDaSessao().catch(() => "");
        const mesma = hashAtual === PEDIDO.hash && String(idColaborador ?? "") === PEDIDO.id;
        if (!mesma) {
          const pend = await SyncService.contarPendenciasSync().catch(() => null);
          if (!pend || pend.indeterminado || pend.total > 0) {
            setErro(
              "Este navegador tem registros de outra conta esperando envio. Abra o Tirvu+ com aquela conta e sincronize antes de entrar com outra.",
            );
            setEtapa("erro");
            return;
          }
          await signOut();
        }
      }

      await saveColaboradorChoice();
      await signIn(PEDIDO.token, PEDIDO.id);
      void syncFirebaseToken(PEDIDO.id);

      // Direto para a Home. A face que já existe no servidor (coletor, TirvuAdm, outro aparelho) vem
      // em segundo plano, depois que o perfil chegar; sem ela, o cadastro é pedido na hora do ponto.
      if (!isBiometricsSetupDone) {
        void refreshUserData()
          .then(() => FaceBiometria.importarDoServidor())
          .then((r) => (r === "importado" ? markBiometricsSetupDone() : undefined))
          .catch(() => {});
      }
      router.replace("/(tabs)/home");
    } catch (e) {
      console.warn("[Entrar] não foi possível entrar com o acesso do TirvuAdm:", e);
      setErro("Não foi possível entrar agora. Tente de novo pelo sistema da sua empresa.");
      setEtapa("erro");
    }
  }, [
    idColaborador,
    isAuthenticated,
    isBiometricsSetupDone,
    markBiometricsSetupDone,
    refreshUserData,
    router,
    signIn,
    signOut,
    syncFirebaseToken,
  ]);

  // Entra sozinho quando a origem é a do TirvuAdm — depois de a sessão anterior (se houver) ser lida.
  useEffect(() => {
    if (etapa === "entrando" && isHydrated && !comecou.current) void entrar();
  }, [etapa, isHydrated, entrar]);

  const irParaOLogin = () => router.replace("/");

  return (
    <div className={s.fundo}>
      <div className={s.cartao} role={etapa === "erro" ? "alert" : "status"}>
        {etapa === "entrando" ? (
          <>
            <span className={s.girando} aria-hidden="true" />
            <span className={s.titulo}>Entrando no Tirvu+...</span>
            <span className={s.texto}>Preparando o seu acesso.</span>
          </>
        ) : etapa === "confirmar" ? (
          <>
            <span className={s.icone}>
              <MaterialCommunityIcons name="account-key-outline" size={28} color="#294956" />
            </span>
            <span className={s.titulo}>Entrar no Tirvu+?</span>
            <span className={s.texto}>
              Você chegou por um link de acesso. Só continue se foi você que entrou agora pelo sistema
              da sua empresa.
            </span>
            <button type="button" className={s.botao} onClick={() => void entrar()} disabled={!isHydrated}>
              Entrar
            </button>
            <button type="button" className={s.botaoSecundario} onClick={irParaOLogin}>
              Cancelar
            </button>
          </>
        ) : (
          <>
            <span className={s.icone}>
              <MaterialCommunityIcons name="alert-circle-outline" size={28} color="#b7791f" />
            </span>
            <span className={s.titulo}>Não foi possível entrar</span>
            <span className={s.texto}>{erro}</span>
            <button type="button" className={s.botao} onClick={irParaOLogin}>
              Ir para o login
            </button>
          </>
        )}
      </div>
    </div>
  );
}
